<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- ============================================================
                 HERO
                 ============================================================ -->
            <section class="mkt-hero-light mkt-hero-watermark" style="padding:80px 0 50px;">
                <div class="mkt-hero-watermark-icon mkt-hero-watermark-icon--right" aria-hidden="true">
                    <q-icon name="gavel" />
                </div>
                <div class="mkt-container text-center" style="position:relative;z-index:1;">
                    <span class="mkt-trust-pill mkt-trust-pill--light q-mb-md" style="display:inline-block;">
                        <q-icon name="gavel" class="mkt-trust-icon" /> For solo attorneys & small firms
                    </span>
                    <h1 class="text-weight-bold q-mt-md q-mb-md" style="font-size:clamp(2rem,4vw,3rem);color:#0F172A;line-height:1.15;">
                        Stop waiting<br>
                        <span class="text-gradient-indigo">for retainers.</span>
                        <span class="visually-hidden"> - Electronic signatures and retainer collection for attorneys</span>
                    </h1>
                    <p class="q-mb-lg" style="font-size:1.1rem;max-width:620px;margin:0 auto;color:#475569;">
                        Send the engagement letter. Collect the retainer. Both in one signing flow -
                        money in your account before the client closes the tab.
                    </p>
                    <div class="row justify-center q-gutter-md mkt-hero-cta-row">
                        <q-btn color="primary" size="lg" label="Start Free" href="/Account/Register?plan=collect"
                            no-caps unelevated icon-right="arrow_forward"
                            style="border-radius:10px;padding:10px 32px;font-weight:600;" />
                        <q-btn outline size="lg" no-caps label="See how it works" href="#workflow"
                            style="border-radius:10px;padding:10px 32px;color:#334155;border-color:#CBD5E1;" />
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 WORKFLOW - 3 step
                 ============================================================ -->
            <section id="workflow" class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">How it works</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">From engagement letter to funded - under an hour</h2>
                    </div>
                    <div class="row q-col-gutter-xl items-stretch">
                        <div class="col-12 col-md-4" v-for="(step, i) in steps" :key="i">
                            <div class="mkt-step-tile">
                                <div class="mkt-step-ribbon">{{ String(i + 1).padStart(2, '0') }}</div>
                                <div class="row items-center q-gutter-sm q-mb-sm">
                                    <q-icon :name="step.icon" size="28px" color="primary" />
                                    <h3 class="text-weight-bold q-ma-none" style="font-size:1.2rem;color:#0a0f1a;line-height:1.2;">{{ step.title }}</h3>
                                </div>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.95rem;line-height:1.6;">{{ step.detail }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 ROI CALLOUT
                 ============================================================ -->
            <section class="mkt-light reveal-fade-in" v-intersection="revealCfg" style="padding:50px 0;">
                <div class="mkt-container-narrow">
                    <div class="mkt-card q-pa-xl" style="border-left:4px solid var(--mkt-indigo);">
                        <div class="row items-center q-col-gutter-lg">
                            <div class="col-12 col-md-4 text-center">
                                <div class="text-weight-bold" style="font-size:3rem;letter-spacing:-0.02em;line-height:1;color:#0F172A;">
                                    <span style="background:linear-gradient(135deg,#4F46E5,#7C3AED);-webkit-background-clip:text;background-clip:text;color:transparent;">$5,000</span>
                                </div>
                                <div class="text-grey-7 q-mt-xs" style="font-size:0.85rem;">retainer collected</div>
                            </div>
                            <div class="col-12 col-md-8">
                                <h3 class="text-weight-bold q-mb-sm" style="font-size:1.15rem;color:#0a0f1a;">Real flow</h3>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.95rem;line-height:1.7;">
                                    9:00 AM - engagement letter sent.
                                    9:47 AM - client signed and paid the retainer.
                                    Funds settle to your connected account by close of business. The audit trail
                                    captures the signed agreement <em>and</em> the payment evidence in one record.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 VERTICAL FEATURES
                 ============================================================ -->
            <section id="features" class="mkt-white reveal-scale" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">Built for legal practice</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">What you get</h2>
                    </div>
                    <div class="row q-col-gutter-lg">
                        <div class="col-12 col-md-6 col-lg-4" v-for="f in features" :key="f.title">
                            <div class="mkt-card q-pa-lg" style="height:100%;">
                                <q-item class="q-pa-none q-mb-sm">
                                    <q-item-section avatar>
                                        <q-icon :name="f.icon" size="32px" :style="`color:${f.color};`" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-weight-bold" style="font-size:1.05rem;color:#0a0f1a;line-height:1.25;">{{ f.title }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.9rem;line-height:1.6;">{{ f.detail }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 PRICING
                 ============================================================ -->
            <section id="pricing" class="mkt-light reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-lg">
                        <div class="mkt-section-eyebrow">Pricing</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">Sign & Collect - built for this workflow</h2>
                        <p class="text-grey-7" style="font-size:1rem;max-width:560px;margin:0 auto;">
                            $39/mo for 50 signings, plus a 2.5% platform fee on collected retainers. Self-serve onboarding.
                        </p>
                    </div>
                    <div style="max-width:400px;margin:0 auto;">
                        <plan-card tier-code="collect"
                                   featured featured-label="For service collection"
                                   icon="payments" accented
                                   cta-label="Start with Sign & Collect"
                                   includes-label="What you get:" />
                    </div>
                    <div class="text-center q-mt-lg">
                        <span style="font-size:0.9rem;color:#475569;">Need higher volume or audit-grade retention?</span>
                        <q-btn flat no-caps color="primary" label="Compare all plans"
                               icon-right="arrow_forward" href="/Home/Pricing"
                               class="q-ml-sm"
                               style="font-size:0.9rem;font-weight:500;border-radius:8px;" />
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 FAQ
                 ============================================================ -->
            <section class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0 80px;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">FAQ</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:1.8rem;color:#0a0f1a;">Common questions from law practices</h2>
                    </div>
                    <q-list>
                        <q-expansion-item v-for="faq in faqs" :key="faq.q" :label="faq.q"
                            header-class="text-weight-bold" style="border-bottom:1px solid rgba(0,0,0,0.06);">
                            <q-card>
                                <q-card-section class="text-grey-7" style="font-size:0.95rem;line-height:1.7;">
                                    {{ faq.a }}
                                </q-card-section>
                            </q-card>
                        </q-expansion-item>
                    </q-list>
                </div>
            </section>

            <!-- ============================================================
                 FINAL CTA
                 ============================================================ -->
            <section class="mkt-cta-gradient reveal-scale" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container text-center">
                    <h2 class="text-white text-weight-bold q-mb-md" style="font-size:1.8rem;">Get the retainer signed and funded today.</h2>
                    <p class="text-white q-mb-lg" style="opacity:0.9;font-size:1rem;">Self-serve. Secure card processing. No long-term contract.</p>
                    <q-btn color="white" text-color="primary" size="lg" label="Start Free" href="/Account/Register?plan=collect"
                        no-caps unelevated icon-right="arrow_forward"
                        style="border-radius:10px;padding:10px 36px;font-weight:600;" />
                </div>
            </section>

            <marketing-footer />
        </q-page-container>
    </q-layout>
</template>

<script>
import PlanCard from '@components/marketing/PlanCard.vue';
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingFooter from '@components/marketing/MarketingFooter.vue';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'ForAttorneysPage',
    mixins: [revealMixin],
    components: { PlanCard, MarketingNav, MarketingFooter },
    data() {
        return {
            mobileNav: false,

            steps: [
                { icon: 'description', title: 'Send the engagement letter',
                    detail: 'Use a saved template (Buyer/Provider roles), drop in client details, hit send.' },
                { icon: 'edit',        title: 'Client signs & pays',
                    detail: 'They sign in browser, then enter card to pay the retainer in the same flow. Mobile-friendly.' },
                { icon: 'account_balance', title: 'Funds + signed agreement',
                    detail: 'Money settles to your Stripe-connected account. The audit trail captures both events together.' }
            ],

            features: [
                { icon: 'payments',         color: '#047857', title: 'Retainer at signing',
                    detail: 'Charge the retainer or place an auth-hold for capture later. Refund the unused balance with one click.' },
                { icon: 'description',      color: '#2563EB', title: 'Engagement letter templates',
                    detail: 'Save your engagement-letter template once. Pre-place signature, initial, and date fields. Reuse with one click.' },
                { icon: 'verified',         color: '#4F46E5', title: 'Tamper-evident audit trail',
                    detail: 'Every signature and every payment event captured with IP, timestamp, and SHA-256 hash chain. Court-ready.' },
                { icon: 'groups',           color: '#B45309', title: 'Multi-attorney + witness flows',
                    detail: 'Add co-counsel signature, witness sign-off, or paralegal review steps. Sequential or parallel.' },
                { icon: 'history',          color: '#0D9488', title: '7-year retention',
                    detail: 'Available on Business - every signed agreement and payment record stored for 7 years for bar compliance.' },
                { icon: 'workspace_premium', color: '#7C3AED', title: 'Branded for your firm',
                    detail: 'Your logo, colors, and reply-to address on the signing ceremony and the email the client receives.' }
            ],

            faqs: [
                { q: 'Can I use this for IOLTA trust account deposits?',
                    a: 'Yes. Connect your Stripe account associated with your IOLTA, and retainers settle directly there. Each transaction is captured in the audit trail with the signed engagement letter - clean evidence for compliance reviews.' },
                { q: 'What happens if I need to refund part or all of a retainer?',
                    a: 'Issue a full or partial refund from the dashboard with one click. The refund event joins the audit trail alongside the original payment, giving you a complete record of the matter.' },
                { q: 'Can I have multiple attorneys sign the engagement letter?',
                    a: 'Yes. Add as many signers as you need - co-counsel, supervising attorney, paralegal review, or client witnesses. Set the order or run them in parallel. The audit trail records each signature separately.' },
                { q: 'How is this different from sending a DocuSign + invoicing separately?',
                    a: 'It collapses two workflows into one. The client signs and pays in the same browser session, so you never have to chase the second half. Plus the payment record is captured against the same matter as the signed agreement.' },
                { q: 'Is the signed engagement letter legally binding?',
                    a: 'Yes - signatures are ESIGN Act and UETA compliant in all 50 states, with hash-chained audit trail and certificate of completion attached to every signed document. Courts have repeatedly upheld electronic engagement letters.' },
                { q: 'What if the retainer card declines?',
                    a: 'The signing flow surfaces the decline immediately. The agreement isn\'t marked complete until payment authorizes successfully. You can also configure auth-hold + capture-later for cases where you want the agreement signed first and the payment confirmed at a later step.' }
            ]
        };
    }
};
</script>

<style scoped>
.mkt-section-eyebrow {
    display: inline-block;
    font-size: 0.8rem;
    font-weight: 700;
    color: #4F46E5;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 6px;
}
</style>
